import { useState, useEffect } from 'react';
import { useNavigate, Link, Outlet, useLocation } from 'react-router-dom';
import api, { clearAuthToken, getAuthToken } from '@/lib/api';
import { toast } from 'sonner';
import { 
  Shield, 
  LayoutDashboard, 
  Users, 
  FileText, 
  Dumbbell,
  Trophy,
  Upload,
  Bug,
  LogOut,
  Menu,
  X
} from 'lucide-react';
import { Button } from '@/components/ui/button';

export default function AdminLayout() {
  const navigate = useNavigate();
  const location = useLocation();
  const [admin, setAdmin] = useState(null);
  const [loading, setLoading] = useState(true);
  const [sidebarOpen, setSidebarOpen] = useState(false);

  useEffect(() => {
    checkAdmin();
  }, []);

  const checkAdmin = async () => {
    const token = getAuthToken();
    if (!token) {
      navigate('/admin/login');
      return;
    }

    try {
      const response = await api.get('/admin/check');
      if (response.data.is_admin) {
        setAdmin(response.data);
      } else {
        toast.error('Admin access required');
        navigate('/admin/login');
      }
    } catch (error) {
      console.error('Admin check failed:', error);
      clearAuthToken();
      navigate('/admin/login');
    } finally {
      setLoading(false);
    }
  };

  const handleLogout = async () => {
    try {
      await api.post('/auth/logout');
      clearAuthToken();
      toast.success('Logged out');
      navigate('/admin/login');
    } catch (error) {
      clearAuthToken();
      navigate('/admin/login');
    }
  };

  const navItems = [
    { path: '/admin/dashboard', label: 'Dashboard', icon: LayoutDashboard },
    { path: '/admin/users', label: 'Users', icon: Users },
    { path: '/admin/games', label: 'Games', icon: Trophy },
    { path: '/admin/games/import', label: 'Import CSV', icon: Upload },
    { path: '/admin/drill-library', label: 'Drill Library', icon: Dumbbell },
    { path: '/admin/audit-logs', label: 'Audit Logs', icon: FileText },
    { path: '/admin/bug-reports', label: 'Bug Reports', icon: Bug },
  ];

  if (loading) {
    return (
      <div className="min-h-screen bg-slate-900 flex items-center justify-center">
        <div className="text-white">Loading...</div>
      </div>
    );
  }

  return (
    <div className="h-screen flex flex-col bg-slate-100 overflow-hidden">
      {/* Mobile Header */}
      <div className="lg:hidden text-white p-4 flex items-center justify-between flex-shrink-0" style={{ backgroundColor: '#13314b' }}>
        <div className="flex items-center space-x-3">
          <img src="/smarter-hoops-wordmark.png" alt="Smarter Hoops" style={{ width: '15%', minWidth: '80px' }} />
          <span className="font-bold text-sm" style={{ fontFamily: "'Barlow Condensed', sans-serif" }}>Admin</span>
        </div>
        <Button variant="ghost" size="sm" onClick={() => setSidebarOpen(!sidebarOpen)}>
          {sidebarOpen ? <X className="w-6 h-6" /> : <Menu className="w-6 h-6" />}
        </Button>
      </div>

      <div className="flex flex-1 min-h-0">
        {/* Sidebar */}
        <aside className={`
          fixed lg:static inset-y-0 left-0 z-50 w-64 bg-slate-900 text-white transform 
          ${sidebarOpen ? 'translate-x-0' : '-translate-x-full'} 
          lg:translate-x-0 transition-transform duration-200
          lg:h-full overflow-y-auto
        `}>
          <div className="p-6 border-b border-slate-700 hidden lg:block">
            <img src="/smarter-hoops-wordmark.png" alt="Smarter Hoops" className="w-full mb-3" />
            <h1 className="font-bold text-lg" style={{ fontFamily: "'Barlow Condensed', sans-serif" }}>Admin Panel</h1>
            {admin?.name && (
              <p className="text-sm text-slate-300 mt-1">{admin.name}</p>
            )}
            {admin?.email && (
              <p className="text-xs text-slate-400">{admin.email}</p>
            )}
          </div>

          <nav className="p-4 space-y-1">
            {navItems.map((item) => {
              const Icon = item.icon;
              const isActive = location.pathname === item.path;
              return (
                <Link
                  key={item.path}
                  to={item.path}
                  onClick={() => setSidebarOpen(false)}
                  className={`
                    flex items-center space-x-3 px-4 py-3 rounded-lg transition-colors
                    ${isActive 
                      ? 'bg-red-600 text-white' 
                      : 'text-slate-300 hover:bg-slate-800 hover:text-white'
                    }
                  `}
                  data-testid={`admin-nav-${item.label.toLowerCase().replace(' ', '-')}`}
                >
                  <Icon className="w-5 h-5" />
                  <span className="font-medium">{item.label}</span>
                </Link>
              );
            })}
            <Button
              onClick={handleLogout}
              variant="ghost"
              className="w-full justify-start text-slate-300 hover:text-white hover:bg-slate-800 px-4 py-3"
              data-testid="admin-logout-button"
            >
              <LogOut className="w-5 h-5 mr-3" />
              <span className="font-medium">Logout</span>
            </Button>
          </nav>
        </aside>

        {/* Main Content — the only scroll container in the admin shell.
            Body/#root are locked to overflow:hidden in index.css to keep
            the in-game tracker's navbars from rubber-banding on iOS, so
            admin pages have to opt in to scroll explicitly. Per-page
            padding stays on the individual pages.

            iOS-specific: `overflow-y: scroll` (not auto) + the explicit
            `-webkit-overflow-scrolling: touch` is what actually makes
            touch-drag scroll work inside a `position: fixed` body on
            mobile Safari. CoachLayout uses the same trick. */}
        <main
          data-testid="admin-main-scroll"
          className="flex-1 min-w-0 min-h-0 overflow-y-scroll"
          style={{
            WebkitOverflowScrolling: 'touch',
            // Generous bottom space so the last row of any admin page
            // sits well clear of the screen edge / iOS home indicator
            // on mobile. Without this, sticky table footers and the
            // last row of long lists feel cramped against the bezel.
            paddingBottom: 'calc(env(safe-area-inset-bottom) + 6rem)',
          }}
        >
          <Outlet />
        </main>
      </div>

      {/* Mobile Overlay */}
      {sidebarOpen && (
        <div 
          className="fixed inset-0 bg-black/50 z-40 lg:hidden" 
          onClick={() => setSidebarOpen(false)}
        />
      )}
    </div>
  );
}
